/**
 * useSelectionStore — Zustand store for node/edge selection and transaction state.
 *
 * This is the shared state that both Canvas and NodeInspector read/write.
 * Extracted from Workbench.tsx useState hooks as part of Phase 0 lite.
 *
 * Migration source (Workbench.tsx line numbers):
 *   selectedNode        (264)
 *   selectedEdgeIds     (352)
 *   selectedTxs         (334)
 *   edgeTxMap           (355)
 *   selectedEdgePair    (356)
 *   txFilter            (268)
 *   activeTab           (265)
 *   panelMode           (266)
 *   nodeDetailMinimized (267)
 */

import { create } from 'zustand';
import type {
    GraphNodeData,
    TransactionEvidence,
    GraphEdgeData,
} from '../types/graph';
import { safeStr } from '../utils/safeStr';

/** Sanitize node data fields that can be objects from API responses (prevents React #185). */
const sanitizeNode = (node: GraphNodeData): GraphNodeData => {
    const n = { ...node };
    if (n.entity_name && typeof n.entity_name !== 'string') n.entity_name = safeStr(n.entity_name) || undefined;
    if (n.entity_type && typeof n.entity_type !== 'string') n.entity_type = safeStr(n.entity_type) || undefined;
    if (n.entity_source && typeof n.entity_source !== 'string') n.entity_source = safeStr(n.entity_source) || undefined;
    if (n.entity_category && typeof n.entity_category !== 'string') n.entity_category = safeStr(n.entity_category) || undefined;
    if (n.entity_country && typeof n.entity_country !== 'string') n.entity_country = safeStr(n.entity_country) || undefined;
    return n;
};

// ── Types ────────────────────────────────────────────────────────────

export interface EdgePair {
    source: string;
    target: string;
}

export type InspectorTab =
    | 'overview'
    | 'counterparties'
    | 'transfers'
    | 'activity'      // legacy alias — maps to Transfers tab
    | 'intelligence'
    | 'actions'
    | 'analysis'
    | 'forensic';

export type TxDirection = 'all' | 'incoming' | 'outgoing';

export interface TxFilter {
    direction: TxDirection;
    token: string;
}

// ── Cross-store reset registry (avoids circular imports) ──
// Other stores call registerNodeChangeCallback() at init to participate in resetOnNodeChange.
const _nodeChangeCallbacks: Array<() => void> = [];
export function registerNodeChangeCallback(fn: () => void) {
    _nodeChangeCallbacks.push(fn);
}

// ── Store ────────────────────────────────────────────────────────────

export interface SelectionState {
    // Node selection
    selectedNodeId: string | null;
    selectedNode: GraphNodeData | null;

    // Edge selection
    selectedEdgeIds: string[];
    selectedEdgePair: EdgePair | null;

    // Transaction selection (for notices, batch actions)
    selectedTxs: TransactionEvidence[];

    // Edge consolidation map: "source->target" → individual txs
    edgeTxMap: Map<string, TransactionEvidence[]>;

    // Canvas ↔ Inspector sync (Canvas writes these, Inspector reads)
    canvasNodeAddresses: Set<string>;
    canvasTxHashes: Set<string>;

    // Transaction filter (shared between tabs that display txs)
    txFilter: TxFilter;

    // Cross-tab counterparty filter: set by CounterpartiesTab "View all", read by TransfersTab
    counterpartyFilter: { address: string; displayName: string } | null;

    // Hover cross-highlight (bidirectional canvas ↔ inspector)
    hoveredNodeId: string | null;
    hoveredSource: 'canvas' | 'inspector' | null;

    // Inspector panel state
    activeTab: InspectorTab;
    panelMode: 'details' | 'analysis';
    nodeDetailMinimized: boolean;

    // ── Actions ──

    /** Select a node (from canvas click or programmatic). Resets edge/tx selection. */
    selectNode: (node: GraphNodeData) => void;

    /** Update the selected node's data in-place (e.g. after triage enrichment). */
    updateSelectedNode: (patch: Partial<GraphNodeData>) => void;

    /** Replace expanded_transactions on the selected node. */
    setExpandedTransactions: (txs: TransactionEvidence[]) => void;

    /** Append transactions to the selected node's expanded_transactions. */
    appendTransactions: (txs: TransactionEvidence[]) => void;

    /** Clear node selection entirely. */
    clearSelection: () => void;

    // Edge selection
    setSelectedEdgeIds: (ids: string[]) => void;
    setSelectedEdgePair: (pair: EdgePair | null) => void;

    // Transaction selection
    toggleTxSelection: (tx: TransactionEvidence) => void;
    setSelectedTxs: (txs: TransactionEvidence[]) => void;
    clearTxSelection: () => void;

    // Edge consolidation
    setEdgeTxMap: (map: Map<string, TransactionEvidence[]>) => void;
    updateEdgeTxMap: (key: string, txs: TransactionEvidence[]) => void;

    // Canvas sync — called by GraphCanvas on Cytoscape events
    syncCanvasState: (nodeAddresses: Set<string>, txHashes: Set<string>) => void;

    // Filter
    setTxFilter: (filter: TxFilter) => void;

    // Cross-tab counterparty filter
    setCounterpartyFilter: (filter: { address: string; displayName: string } | null) => void;
    clearCounterpartyFilter: () => void;

    // Inspector panel
    setActiveTab: (tab: InspectorTab) => void;
    setPanelMode: (mode: 'details' | 'analysis') => void;
    setNodeDetailMinimized: (minimized: boolean) => void;

    // Hover cross-highlight
    setHoveredNode: (id: string | null, source: 'canvas' | 'inspector') => void;

    /**
     * Reset state that should clear when selectedNode changes.
     * Mirrors the reset useEffect in Workbench.tsx (lines 805-824).
     */
    resetOnNodeChange: () => void;
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
    // ── Initial state ──
    selectedNodeId: null,
    selectedNode: null,
    selectedEdgeIds: [],
    selectedEdgePair: null,
    selectedTxs: [],
    edgeTxMap: new Map(),
    canvasNodeAddresses: new Set(),
    canvasTxHashes: new Set(),
    txFilter: { direction: 'all', token: 'all' },
    counterpartyFilter: null,
    hoveredNodeId: null,
    hoveredSource: null,
    activeTab: 'overview',
    panelMode: 'details',
    nodeDetailMinimized: false,

    // ── Actions ──

    selectNode: (node) => {
        const prev = get().selectedNodeId;
        set({
            selectedNodeId: node.id,
            selectedNode: sanitizeNode(node),
            // Clear edge state on node selection (mirrors Workbench line 1725)
            selectedEdgeIds: [],
            selectedEdgePair: null,
        });
        // If node actually changed, run the reset
        if (prev !== node.id) {
            get().resetOnNodeChange();
        }
    },

    updateSelectedNode: (patch) => {
        const current = get().selectedNode;
        if (!current) return;
        set({ selectedNode: sanitizeNode({ ...current, ...patch }) });
    },

    setExpandedTransactions: (txs) => {
        const current = get().selectedNode;
        if (!current) return;
        set({
            selectedNode: {
                ...current,
                expanded_transactions: txs,
                total_expanded_txs: txs.length,
            },
        });
    },

    appendTransactions: (txs) => {
        const current = get().selectedNode;
        if (!current) return;
        const existing = current.expanded_transactions || [];
        // Deduplicate by tx_hash
        const existingHashes = new Set(existing.map(t => t.tx_hash));
        const newTxs = txs.filter(t => !existingHashes.has(t.tx_hash));
        const merged = [...existing, ...newTxs];
        set({
            selectedNode: {
                ...current,
                expanded_transactions: merged,
                total_expanded_txs: merged.length,
            },
        });
    },

    clearSelection: () =>
        set({
            selectedNodeId: null,
            selectedNode: null,
            selectedEdgeIds: [],
            selectedEdgePair: null,
            selectedTxs: [],
        }),

    setSelectedEdgeIds: (ids) => set({ selectedEdgeIds: ids }),

    setSelectedEdgePair: (pair) => set({ selectedEdgePair: pair }),

    toggleTxSelection: (tx) => {
        const current = get().selectedTxs;
        const idx = current.findIndex(t => t.tx_hash === tx.tx_hash);
        if (idx >= 0) {
            set({ selectedTxs: current.filter((_, i) => i !== idx) });
        } else {
            set({ selectedTxs: [...current, tx] });
        }
    },

    setSelectedTxs: (txs) => set({ selectedTxs: txs }),

    clearTxSelection: () => set({ selectedTxs: [] }),

    setEdgeTxMap: (map) => set({ edgeTxMap: map }),

    updateEdgeTxMap: (key, txs) => {
        const current = get().edgeTxMap;
        const next = new Map(current);
        next.set(key, txs);
        set({ edgeTxMap: next });
    },

    syncCanvasState: (nodeAddresses, txHashes) =>
        set({ canvasNodeAddresses: nodeAddresses, canvasTxHashes: txHashes }),

    setTxFilter: (filter) => set({ txFilter: filter }),

    setCounterpartyFilter: (filter) => set({ counterpartyFilter: filter }),
    clearCounterpartyFilter: () => set({ counterpartyFilter: null }),

    setActiveTab: (tab) => set({ activeTab: tab }),

    setPanelMode: (mode) => set({ panelMode: mode }),

    setNodeDetailMinimized: (minimized) => set({ nodeDetailMinimized: minimized }),

    setHoveredNode: (id, source) => set({ hoveredNodeId: id, hoveredSource: id ? source : null }),

    resetOnNodeChange: () => {
        set({
            panelMode: 'details',
            selectedTxs: [],
            counterpartyFilter: null,
            hoveredNodeId: null,
            hoveredSource: null,
            // Don't reset activeTab to 'overview' here — let the component decide
            // (Workbench only resets if on 'intelligence' tab, line 820-822)
        });

        // Cross-store resets (Phase 0 full)
        // Uses callback registry to avoid circular imports between stores
        _nodeChangeCallbacks.forEach(fn => fn());
    },
}));

// Expose store on window for E2E test introspection. Mirrors the
// `window.__cy` and `window.__useGraphStore` patterns. Read-only.
if (typeof window !== 'undefined') {
    (window as any).__useSelectionStore = useSelectionStore;
}
